


















































































































































































import Vue, { PropType } from 'vue';
import { mapGetters, mapActions } from 'vuex';

import {
  IQuote,
  IQuoteDocument,
  IQuoteDocumentData,
  IQuotePackage,
  IQuotePackageDocumentData,
  QuotePackage,
  QuotePackageDocument,
  IMoveDragData,
  QuotePackageSectionEnum
} from '@/models';
import {
  FieldErrors,
  ISystemError,
  ISystemErrorData,
  SystemError
} from '@/models/error/index';
import { IDocumentSections } from './QuoteDocumentPackageBuilder.vue';
import { diffDeep } from '@/utils/helpers';
import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';

import Draggable from 'vuedraggable';
import DocumentSelectionPicker from '@/components/document/DocumentSelectionPicker.vue';
import QuoteExpiredLabel from '../quote-common/QuoteExpiredLabel.vue';
import QuoteLayoutMixin from '@/mixins/quotes/QuoteLayoutMixin';

export default mixins(auth, QuoteLayoutMixin).extend({
  name: 'QuoteDocumentPackageBuilder',

  model: {
    prop: 'value',
    event: 'input'
  },

  props: {
    value: Array as PropType<IDocumentSections[]>,

    quotePackage: Object as PropType<IQuotePackage>,
    selectedSectionIndex: Number,
    selectedDocumentIndex: Number
  },

  components: {
    DocumentSelectionPicker,
    Draggable,
    QuoteExpiredLabel
  },

  data() {
    return {
      lazyValue: this.value,
      loading: false,
      error: null as ISystemError | null,
      localQuotePackage: null as IQuotePackage | null,
      documentSideMenuIndexList: [
        QuotePackageSectionEnum.Cover - 1,
        null, // placeholder for proposal summary
        QuotePackageSectionEnum.SpecSheets - 1
      ]
    };
  },

  computed: {
    ...mapGetters({
      getQuotePackageById: 'quote/getQuotePackageById'
    }),

    internalValue: {
      get(): any {
        return this.lazyValue;
      },
      set(val: any) {
        this.lazyValue = val;
        this.$emit('input', val);
      }
    },

    getMachineOrAutomationQuote(): undefined | IQuote {
      return this.quotePackage.quotes?.find(quote => this.useMachineLayout(quote));
    },

    getVentilationQuote(): undefined | IQuote {
      return this.quotePackage.quotes?.find(quote =>
        quote.isVentilationQuote()
      );
    }
  },

  watch: {
    quotePackage: {
      handler(value) {
        this.localQuotePackage = value;
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions({
      updateQuotePackage: 'quote/updateQuotePackage'
    }),

    findQuotePackageDocumentIndex(packageDocument: IQuotePackageDocumentData) {
      return this.localQuotePackage!.package_documents!.findIndex(document => {
        if (packageDocument.id) {
          return document.id == packageDocument.id;
        }

        if (packageDocument.quote_document) {
          const documentId =
            typeof packageDocument.quote_document == 'number'
              ? packageDocument.quote_document
              : (packageDocument.quote_document as IQuoteDocumentData).id;

          return (
            documentId == (document.quote_document as IQuoteDocumentData).id &&
            packageDocument.order == document.order &&
            packageDocument.section == document.section
          );
        }
        return false;
      });
    },

    setSelectedDocumentSectionIndex(
      sectionIndex: number,
      documentIndex: number
    ) {
      this.$emit(
        'set-selected-document-section-index',
        sectionIndex,
        documentIndex
      );
    },

    isDocumentSelected(sectionIndex: number, documentIndex: number) {
      return (
        sectionIndex == this.selectedSectionIndex &&
        documentIndex == this.selectedDocumentIndex
      );
    },

    setUploadedDocument(
      document: IQuotePackageDocumentData,
      section: number,
      index: number
    ) {
      this.internalValue[section].package_documents.splice(index, 1, document);

      this.localQuotePackage!.package_documents!.push(
        new QuotePackageDocument(document)
      );

      this.saveQuotePackage(section, index);
    },

    addDocument(section: IDocumentSections, sectionIndex: number) {
      const order =
        section.package_documents.length > 0
          ? section.package_documents[section.package_documents.length - 1]
              .order! + 1
          : 1;

      section.package_documents.push({
        order: order,
        section: sectionIndex + 1,
        quote_package: this.quotePackage.id
      });

      this.setSelectedDocumentSectionIndex(
        sectionIndex,
        section.package_documents.length - 1
      );
    },

    async removeDocument(section: number, index: number) {
      const quotePackage = this.internalValue[section].package_documents[index];

      this.internalValue[section].package_documents.splice(index, 1);

      if (quotePackage.quote_document) {
        const packageIndex = this.findQuotePackageDocumentIndex(quotePackage);

        if (packageIndex != -1) {
          this.localQuotePackage!.package_documents!.splice(packageIndex, 1);

          await this.saveQuotePackage(section);
        }
      }

      this.setSelectedDocumentSectionIndex(section, 0); //reselect the first document in the list
    },

    reorderQuoteDocuments(
      movedData: IMoveDragData<IQuotePackageDocumentData>,
      section: number
    ) {
      this.localQuotePackage = this.getQuotePackageById(
        this.quotePackage.id
      ) as IQuotePackage;

      const slice =
        movedData.moved.newIndex <= movedData.moved.oldIndex
          ? { start: movedData.moved.newIndex, end: movedData.moved.oldIndex }
          : { start: movedData.moved.oldIndex, end: movedData.moved.newIndex };

      for (let index = slice.start; index < slice.end + 1; index++) {
        const packageDocument = this.internalValue[section].package_documents[
          index
        ];

        const quotePackageIndex = this.findQuotePackageDocumentIndex(
          packageDocument
        );

        if (quotePackageIndex != -1) {
          this.localQuotePackage!.package_documents![quotePackageIndex].order =
            index + 1;
        } else {
          this.internalValue[section].package_documents[index].order =
            index + 1;
        }
      }

      this.saveQuotePackage(section, movedData.moved.newIndex);
    },

    addSelectedDocument(
      document: IQuoteDocument,
      sectionIndex: number,
      documentIndex: number
    ) {
      const quotePackage = this.internalValue[sectionIndex].package_documents[
        documentIndex
      ];

      this.localQuotePackage!.package_documents!.push(
        new QuotePackageDocument({
          order: quotePackage.order,
          section: quotePackage.section,
          quote_package: this.localQuotePackage!.id!,
          quote_document: document.id
        })
      );

      this.saveQuotePackage(sectionIndex, documentIndex);
    },

    async saveQuotePackage(sectionIndex: number, documentIndex = 0) {
      let documentInstance: QuotePackage | null = null;

      const payloadPackageDocument = this.localQuotePackage!.package_documents?.map(
        packageDocuments => {
          const document =
            typeof packageDocuments.quote_document == 'number'
              ? packageDocuments.quote_document
              : (packageDocuments.quote_document as IQuoteDocumentData).id;

          return {
            ...packageDocuments,
            quote_document: document
          };
        }
      );

      try {
        documentInstance = new QuotePackage({
          ...this.localQuotePackage!,
          package_documents: payloadPackageDocument
        });
      } catch (error) {
        if (SystemError.isSystemError(error)) {
          this.error = error;
        }
      }

      if (documentInstance != null) {
        this.$emit('saving-quote-package', sectionIndex);

        const payload = diffDeep(
          documentInstance,
          this.getQuotePackageById(documentInstance.id),
          true
        );

        await this.updateQuotePackage(payload)
          .then(() => {
            this.localQuotePackage = this.getQuotePackageById(
              this.localQuotePackage!.id
            );
          })
          .catch(error => {
            const systemErrorData: ISystemErrorData = {
              name: error.name,
              message: error.message
            };

            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              systemErrorData.message = error.response.statusText;
              if (error.response.data.detail) {
                systemErrorData.detail = error.response.data.detail;
              } else if (FieldErrors.isFieldError(error.response.data)) {
                systemErrorData.fieldErrors = new FieldErrors(
                  error.response.data
                );
              }
            } else if (error.request) {
              // The request was made but no response was received (Can happen on 500 error)
              systemErrorData.detail =
                'There was an error in processing your request. Please check with IT Admin';
            }

            this.error = new SystemError(systemErrorData);

            this.$emit('system-error-occurred', this.error);
          })
          .finally(() => {
            this.$emit(
              'quote-package-save-completed',
              sectionIndex,
              documentIndex
            );
          });
      }
    }
  }
});
